import React, { createContext, useContext, useState, useEffect } from 'react';

interface Workspace {
  id: string;
  subdomain: string;
  name: string;
  pages: string[] | null;
  tabs: Record<string, string[]> | null;
}

interface WorkspaceContextType {
  workspace: Workspace | null;
  loading: boolean;
}

const DEFAULT_WORKSPACE: Workspace = {
  id: 'full',
  subdomain: '',
  name: 'SuryaIQ Platform',
  pages: null,
  tabs: null,
};

const LS_KEY = 'suryaiq_workspace';

const WorkspaceContext = createContext<WorkspaceContextType>({
  workspace: null,
  loading: true,
});

export function useWorkspace() {
  return useContext(WorkspaceContext);
}

export function getSubdomain(): string {
  const hostname = window.location.hostname;
  // localhost / IP / bare domain → no subdomain
  if (hostname === 'localhost' || hostname === '127.0.0.1' || !hostname.includes('.')) {
    return '';
  }
  // e.g. "pilot-ops.suryaiq.com" → "pilot-ops"
  // e.g. "suryaiq.com" → "" (no subdomain)
  // e.g. "www.suryaiq.com" → "www" (handled as fallback to full)
  const parts = hostname.split('.');
  if (parts.length <= 2) return ''; // bare domain like suryaiq.com
  const sub = parts[0];
  if (sub === 'www' || sub === 'staging') return ''; // www and staging are not workspaces
  return sub;
}

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [workspace, setWorkspace] = useState<Workspace | null>(() => {
    try {
      const cached = localStorage.getItem(LS_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(!workspace);

  useEffect(() => {
    const subdomain = getSubdomain();

    fetch(`/api/workspaces/resolve?subdomain=${encodeURIComponent(subdomain)}`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: Workspace) => {
        setWorkspace(data);
        try {
          localStorage.setItem(LS_KEY, JSON.stringify(data));
        } catch {
          // localStorage unavailable
        }
      })
      .catch(() => {
        // On error, fall back to cached or default
        if (!workspace) {
          setWorkspace(DEFAULT_WORKSPACE);
        }
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <WorkspaceContext.Provider value={{ workspace, loading }}>
      {children}
    </WorkspaceContext.Provider>
  );
}
